import { LOGIN_FAIL, LOGIN_REQUEST, LOGIN_SUCCESS, OPEN_LOGIN_MODAL } from './actions';
import { completeLoginModal } from './modal';
import { createSelector } from 'reselect';
import { fetchBidderCardStatusIfNeeded } from './bidderCardStatus';
import { fetchBidderPreferencesIfNeeded } from './bidderPreference';
import { fetchCertificatesIfNeeded } from './certCapture';
import { fetchConversationFoldersIfNeeded } from './conversationFolders';
import { fetchLAPaymentCardIfNeeded } from './creditCard';
import { fetchSmsAlertsIfNeeded } from './smsAlerts';
import { fetchUpcomingBidderBiddingIfNeeded } from './bidderBiddingInfoUpcoming';
import { getDeployment } from './config';
import { getLoginAnalytics } from './analytics';
import { GlobalState } from '../rootReducer';
import { OPEN_LOGIN_MODAL_FROM_CHECKER } from './shouldLogin';
import { postLogin } from '@/redux/api/bidder';
import resellerTypes from '@/enums/resellerTypes';

/* reducer */
const DEFAULT_STATE = {
    error: false,
    errorMessage: '',
    submitted: false,
    success: false,
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOGIN_FAIL:
            const protectAccount = action.payload === 'Failed to authenticate a';
            return {
                ...state,
                error: true,
                errorMessage: protectAccount ? 'protect' : action.payload.message,
                submitted: false,
                success: false,
            };
        case LOGIN_REQUEST:
            return {
                ...state,
                error: false,
                errorMessage: '',
                submitted: true,
                success: false,
            };
        case LOGIN_SUCCESS:
            return {
                ...state,
                submitted: false,
                success: true,
            };
        case OPEN_LOGIN_MODAL:
        case OPEN_LOGIN_MODAL_FROM_CHECKER:
            return {
                ...state,
                error: false,
                errorMessage: '',
                submitted: false,
                success: false,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.login;

export const getUiLogin = createSelector(stateSelector, (state) => state);

/* ACTION CREATORS */
const loginFail = (emailAddress, error) => ({
    error: true,
    meta: { emailAddress },
    payload: error,
    type: LOGIN_FAIL,
});

const loginRequest = (emailAddress) => ({
    payload: emailAddress,
    type: LOGIN_REQUEST,
});

const loginSuccess = (loginData) => ({
    meta: { ...getLoginAnalytics(loginData.bidder, loginData.resellerType), actionTime: Date.now() },
    payload: loginData,
    type: LOGIN_SUCCESS,
});

export const submitLogin = (
    emailAddress: string | null | undefined,
    password: string | null | undefined,
    mainhostLogin: any = {}
) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        const username = emailAddress || mainhostLogin.authNuid;
        dispatch(loginRequest(username));

        const response = await postLogin({ deployment, mainhostLogin, password, username });
        await dispatch(completeLoginModal());
        await dispatch(
            loginSuccess({
                ...response.data,
                bidder: { ...response.data.bidder, ANMS: response.data.annualNMS },
            })
        );

        dispatch(fetchConversationFoldersIfNeeded());
        dispatch(fetchSmsAlertsIfNeeded());
        dispatch(fetchLAPaymentCardIfNeeded());
        dispatch(fetchBidderCardStatusIfNeeded());
        dispatch(fetchBidderPreferencesIfNeeded());
        if (response.data.resellerType === resellerTypes.RESELLER) {
            dispatch(fetchCertificatesIfNeeded());
        }

        return Promise.all([dispatch(fetchUpcomingBidderBiddingIfNeeded())]);
    } catch (error) {
        dispatch(loginFail(emailAddress, error));
    }
};
